import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  createElement,
  useCallback,
  useMemo,
} from 'react';
import {
  styled,
  createGlobalStyle,
  ThemeProvider,
  css,
} from 'styled-components';
import { setup } from 'goober';
import { shouldForwardProp } from 'goober/should-forward-prop';
import { DatePicker } from '@hn-ui/datepickers';
import { BlueExternalLink } from '@hn-ui/links';
import {
  format,
  startOfWeek,
  endOfWeek,
  getMonth,
  isThisWeek,
  isBefore,
  formatISO,
  parseISO,
} from 'date-fns';
import { Tooltip } from '@hn-ui/tooltips';
import { Modal } from '@hn-ui/modals';
import { useToggle } from '@hn-front/utils';
import TargetInfo from './TargetInfo';
import { Heading, CenteredHeading, HeadingTxt, InfoBtn } from './Common';
import { getISOWeekDays, GradesAndTBD } from './utils';
import Summary from './Summary';
import StudentList, { DayRow, day, Columns } from './StudentList';
import DatePickerInput from './DatePickerInput';
import { DashboardHeading, DashboardDescription } from '../Common';
import { AssessmentTestHelp } from '../dashboards/yearly/AssessmentTestHelp';
import defaultTheme from '../../../landing/components/core/themes/theme';
import { escapeSpecialSymbols } from '../../../components/Common/utils';
import { HelpIcon } from '../Common/Icons';
import { InviteColleaguesModal } from './InviteColleaguesModal';
import {
  useWeeklyDashboardAnalytics,
  WeeklyDashboardEvents,
} from './hooks/useWeeklyDashboardAnalytics';
import { StuckStudents, TooltipContent } from './StuckStudents/StuckStudents';
import { useStuckStudents } from './hooks/useStuckStudents';
import { buildApiUrl } from '../../../shared_app/utils/buildApiUrl';

setup(
  createElement,
  undefined,
  undefined,
  shouldForwardProp((prop) => prop[0] !== '$')
);

const GlobalStyle = createGlobalStyle`
  html {
    --webkit-text-size-adjust: none;
  }
  html * {
    max-height: 100000px;
  }
  :root {
    --main-font: 'Noto Sans', sans-serif;
  }
  a {
    text-decoration: none;
    &:hover {
      text-decoration: none;
    }
  }

  .react-datepicker {
    box-sizing: border-box;
  }
`;

const DatePickerContainer = styled.div`
  display: flex;
`;

const PositionedSummary = styled(Summary)`
  margin-bottom: 26px; /* 43 - 17 = 26 */
`;

const PositionedInfoBtn = styled(InfoBtn)`
  display: inline-flex;

  position: absolute;

  margin: -2px 0 0 4px;
`;

const ScHelpIcon = styled(HelpIcon)`
  margin: 0 0 -2px 4px;

  vertical-align: bottom;
`;

const Day = styled.div`
  ${day}
  ${HeadingTxt}

  margin-right: 6px;

  text-align: center;
  text-transform: none;
`;

const StyledHeading = styled(Heading)`
  margin-bottom: 18px;
`;

const PosCenteredHeading = styled(CenteredHeading)`
  margin-left: -10.8px;
`;

const BackTo = styled.p`
  width: 100vw;

  position: fixed;
  top: 0;
  left: 0;
  z-index: 12;

  margin: 0;
  padding: 8px 0;

  color: #242527;
  font-family: var(--main-font);
  font-size: 16px;
  line-height: 24px;
  text-align: center;

  background: #e3f2fc;
`;

const StudentListHeader = styled.div`
  ${Columns}

  position: sticky;
  top: ${({ isWeekInThePast }) => (isWeekInThePast ? '40px' : '0')};
  z-index: 11;

  padding-top: 17px;

  background: ${({ theme }) => theme.colors.black100};
  box-shadow: rgb(0 0 0 / 10%) 0px 16px 16px -16px;
`;

const CalendarNotice = styled.p`
  font-family: var(--main-font);
  font-size: 16px;
  line-height: 24px;
  color: #242527;
  margin-top: 8px;
  margin-left: 26px;
`;

const ButtonLink = styled.button`
  margin: 0;
  padding: 0;

  color: #42abe9;
  font-size: 16px;
  line-height: 24px;

  background: none;
  border: none;
  cursor: pointer;

  transition: 0.2s color ease;

  &:hover {
    color: #1a95de;
  }
`;

const AboveSummaryContainer = styled.div`
  display: flex;
  justify-content: space-between;

  margin: 30px 7px 28px 7px;
`;

const WeekRange = styled.p`
  color: #242527;
  font-family: var(--main-font);
  font-size: 24px;
  line-height: 32px;
  letter-spacing: -0.2px;

  border: none;
`;

const PositionedWeekRange = styled(WeekRange)`
  margin-top: 4px;
  margin-right: 10px;

  font-family: var(--main-font);
`;

const ModifiedDashboardDescription = styled(DashboardDescription)`
  color: #242527;
  font-size: 11px;

  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

function groupBy(list, keyGetter) {
  const map = new Map();
  list.forEach((item, id) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [{ ...item, id }]);
    } else {
      collection.push({ ...item, id });
    }
  });
  return map;
}

const objectToMap = (obj) => {
  const keys = Object.keys(obj);
  const map = new Map();
  for (let i = 0; i < keys.length; i += 1) {
    map.set(keys[i], obj[keys[i]]);
  }
  return map;
};

const ModalWindow = styled(Modal)`
  div,
  section {
    box-sizing: border-box;
  }

  display: block;
  overflow: auto;

  > div:nth-child(2) {
    width: 952px;
    margin: 30px auto;
  }
`;

const ScStuckStudents = styled(StuckStudents)`
  margin-top: 22px;

  ${({ $popup }) =>
    $popup &&
    css`
      position: relative;
      z-index: 12;
    `};
`;

const Overlay = styled.div`
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.3);
    z-index: 11;
    width: 100vw;
    height: 100vh;
  }
`;

const moyUrl =
  'https://help.happynumbers.com/en/articles/5055691-set-up-your-mid-year-test-with-happy-numbers';
const eoyUrl =
  'https://help.happynumbers.com/en/articles/5061212-set-up-your-end-of-year-test-with-happy-numbers';

const WeeklyDashboard = ({
  classId,
  role,
  schoolId,
  startWeekDate,
  teacherName,
  isTeacherPremium,
  inviteColleaguesTest,
}) => {
  const currentDate = useMemo(
    () => (startWeekDate ? parseISO(startWeekDate) : new Date()),
    [startWeekDate]
  );
  const [startDate, setStartDate] = useState(currentDate);
  const { isToggled: isInfoModalOpen, toggle: toggleInfoModalOpen } =
    useToggle(false);
  const { isToggled: isAccuracyModalOpen, toggle: toggleAccuracyModalOpen } =
    useToggle(false);
  const [isHeaderAtTheTop, setIsHeaderAtTheTop] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [weeklyData, setWeeklyData] = useState({
    students: [
      [
        0,
        {
          grade: 'Grade 1',
          full_name: 'A',
          link: '',
          accuracy: 0,
          next_grade_readiness: 0,
          activity_calendar: [],
          placement_test: {
            status: '',
          },
          weekly_plan: 0,
          area_of_focus: {
            tag_name: 'Operations',
            position_abbreviation: 'Op.',
          },
        },
      ],
    ],
    active_students_count: [],
    online_students_count: [],
    beginning: new Date().toString(),
    weekly_target_completion: {
      not_started: [],
      '1/4_completed': [],
      half_completed: [],
      '3/4_completed': [],
      completed: [],
    },
    active_students_by_days: {},
    weekly_plan_completed: [],
    stuck_students: [],
    stuck_students_popup_seen: false,
  });
  const studentListHeaderRef = useRef(null);
  const handleChangeWeek = async (date) => {
    const startOfWeekDate = startOfWeek(date, { weekStartsOn: 1 });
    const formattedDate = formatISO(startOfWeekDate, {
      representation: 'date',
    });
    setIsLoading(true);
    setIsError(false);
    const url = buildApiUrl(
      schoolId
        ? `/api/teachers/groups/${classId}/weekly_report?date=${formattedDate}&role=${role}&school_id=${schoolId}`
        : `/api/teachers/groups/${classId}/weekly_report?date=${formattedDate}&role=${role}`
    );

    let response;
    try {
      response = await fetch(url);
      const data = await response.json();
      const updatedData = data
        ? JSON.parse(escapeSpecialSymbols(JSON.stringify(data)))
        : data;
      setWeeklyData(updatedData);

      setStartDate(startOfWeekDate);
      setIsLoading(false);
    } catch (error) {
      setIsError(true);
      setIsLoading(false);
      if (window.Sentry && ![401, 403].includes(response?.status))
        window.Sentry.captureException(error);
    }
  };

  useEffect(() => {
    handleChangeWeek(startDate);
  }, []);

  const checkTopPos = () => {
    const { top } = studentListHeaderRef.current.getBoundingClientRect();
    const topRowHeight = 40;
    if (top <= topRowHeight && isHeaderAtTheTop === false) {
      setIsHeaderAtTheTop(true);
    } else if (top > topRowHeight && isHeaderAtTheTop === true) {
      setIsHeaderAtTheTop(false);
    }
  };

  let ticking = false;

  const handleDocumentScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        checkTopPos();
        ticking = false;
      });

      ticking = true;
    }
  };

  const sendGAEvent = useWeeklyDashboardAnalytics();

  useLayoutEffect(() => {
    document.addEventListener('scroll', handleDocumentScroll);
    return () => {
      document.removeEventListener('scroll', handleDocumentScroll);
    };
  }, [isHeaderAtTheTop]);

  useEffect(() => {
    sendGAEvent(WeeklyDashboardEvents.pageView);
  }, [sendGAEvent]);

  const minDate = new Date(weeklyData.beginning);

  const datepickerRef = useRef(null);
  const studentsMap = objectToMap(weeklyData.students);
  const groupedStudents = groupBy(studentsMap, (student) => student.grade);
  const filteredGrades = GradesAndTBD.filter((grade) =>
    groupedStudents.get(grade)
  );
  const start = startOfWeek(startDate, { weekStartsOn: 1 });
  const end = endOfWeek(startDate, { weekStartsOn: 1 });
  const weekRange =
    getMonth(start) === getMonth(end)
      ? `${format(start, 'MMMM')} ${format(start, 'd')} \u2013 ${format(
          end,
          'd'
        )}`
      : `${format(start, 'MMMM d')} \u2013 ${format(end, 'MMMM d')}`;
  const days = getISOWeekDays(startDate);
  const isCurrentWeek = isThisWeek(startDate, { weekStartsOn: 1 });
  const startOfTheCurrentWeek = startOfWeek(currentDate, { weekStartsOn: 1 });
  const isWeekInThePast = isBefore(start, startOfTheCurrentWeek);
  const ISODate = startDate.toISOString().substring(0, 10);

  const onCalendarClick = useCallback(() => {
    sendGAEvent(WeeklyDashboardEvents.datepickerClick);
  }, [sendGAEvent]);

  const [isInviteColleaguesShow, setInviteColleaguesShow] = useState(true);
  const allowedModes = ['grade2ExceptA', 'grade2ExceptB', 'grade2A', 'grade2B'];

  const {
    showStuckPopup,
    showStuckStudents,
    stuckStudents,
    toggleStuckPopup,
    refStuckPopup,
    refStuckContainer,
  } = useStuckStudents(weeklyData);

  return (
    <ThemeProvider theme={defaultTheme}>
      <GlobalStyle />
      {showStuckPopup && showStuckStudents && <Overlay />}
      <DashboardHeading>Weekly Dashboard</DashboardHeading>
      <ModifiedDashboardDescription>
        Gain better understanding of how your class and individual students are
        performing on a weekly basis.{' '}
        <BlueExternalLink href='https://help.happynumbers.com/en/articles/4786814-teacher-weekly-dashboard'>
          Learn more
        </BlueExternalLink>
      </ModifiedDashboardDescription>
      {showStuckStudents && (
        <Tooltip
          ref={refStuckPopup}
          content={
            <TooltipContent
              showNewLabel
              onLinkClick={() => toggleStuckPopup(false)}
            />
          }
          maxWidth='100%'
          visible={showStuckPopup}
          interactive={showStuckPopup}
          placement='top-start'
        >
          <ScStuckStudents
            data={stuckStudents}
            $popup={showStuckPopup}
            ref={refStuckContainer}
          />
        </Tooltip>
      )}
      <AboveSummaryContainer>
        <DatePickerContainer>
          <PositionedWeekRange>{weekRange}</PositionedWeekRange>
          <DatePicker
            type='week'
            startDate={start}
            endDate={end}
            onChange={([date]) => handleChangeWeek(date)}
            customInput={<DatePickerInput ref={datepickerRef} />}
            minDate={minDate}
            maxDate={currentDate}
            onInputClick={onCalendarClick}
            popperOffset={[0, 0]}
            popperPlacement='bottom-start'
          />
          {isWeekInThePast && (
            <CalendarNotice>
              You’re looking at a week in the past.{' '}
              <ButtonLink
                type='button'
                onClick={() => {
                  handleChangeWeek(currentDate);
                }}
              >
                Back to current week
              </ButtonLink>
            </CalendarNotice>
          )}
          {isLoading && <CalendarNotice>Loading....</CalendarNotice>}
          {isError && (
            <CalendarNotice>
              There was a problem connecting to the server
            </CalendarNotice>
          )}
        </DatePickerContainer>
      </AboveSummaryContainer>
      <PositionedSummary
        data={weeklyData}
        days={days}
        isCurrentWeek={isCurrentWeek}
        toggleModal={toggleInfoModalOpen}
      />
      {isWeekInThePast && isHeaderAtTheTop && (
        <BackTo>
          You’re looking at a week in the past ({weekRange}
          ).{' '}
          <ButtonLink
            type='button'
            onClick={() => {
              handleChangeWeek(currentDate);
            }}
          >
            Back to current week
          </ButtonLink>
        </BackTo>
      )}
      <StudentListHeader
        ref={studentListHeaderRef}
        isWeekInThePast={isWeekInThePast}
      >
        <div />
        <StyledHeading>
          <br />
          Student
        </StyledHeading>
        <div>
          <PosCenteredHeading>
            Daily Time
            <Tooltip
              content={`Number of minutes student spent
                  during each day of the week`}
              maxWidth='none'
            >
              <ScHelpIcon />
            </Tooltip>
          </PosCenteredHeading>
          <DayRow>
            {days.map(({ day: weekDay, abbr }) => (
              <Day key={weekDay}>{abbr}</Day>
            ))}
          </DayRow>
        </div>
        <StyledHeading>
          {`Time
          Target`}
          <PositionedInfoBtn
            aria-label='How target completion is calculated'
            onClick={toggleInfoModalOpen}
          />
        </StyledHeading>
        <StyledHeading>
          {`Task
            target`}
          <PositionedInfoBtn
            aria-label='How target completion is calculated'
            onClick={toggleInfoModalOpen}
          />
        </StyledHeading>
        <Heading>Tasks completed</Heading>
        <Heading>Weekly accuracy</Heading>
        <Heading>
          {`Next grade
          readiness`}
          <Tooltip
            content={`Estimated number of weeks until next grade
                readiness calculated based on Time and
                Task targets completion.`}
            maxWidth='none'
          >
            <ScHelpIcon />
          </Tooltip>
        </Heading>
        <Heading>
          {`Current
          area of focus`}
          <Tooltip
            content={`The grade, domain, module, and topic
                in which a student is working.`}
            maxWidth='none'
          >
            <ScHelpIcon />
          </Tooltip>
        </Heading>
      </StudentListHeader>
      {filteredGrades.map((grade) => (
        <StudentList
          key={grade}
          role={role}
          schoolId={schoolId}
          heading={grade}
          students={groupedStudents.get(grade)}
          days={days}
          chosenDate={startDate}
          isCurrentWeek={isCurrentWeek}
          ISODate={ISODate}
          toggleModal={toggleAccuracyModalOpen}
          isModalOpen={isAccuracyModalOpen}
          isWeekInThePast={isWeekInThePast}
        />
      ))}
      <ModalWindow
        open={isAccuracyModalOpen}
        onClose={toggleAccuracyModalOpen}
        showOverlay
      >
        <AssessmentTestHelp
          isTeacherPremium={isTeacherPremium}
          role={role}
          teacherName={teacherName}
          moyUrl={moyUrl}
          eoyUrl={eoyUrl}
        />
      </ModalWindow>
      <ModalWindow
        open={isInfoModalOpen}
        onClose={toggleInfoModalOpen}
        showOverlay={true}
      >
        <TargetInfo groupId={classId} />
      </ModalWindow>
      {allowedModes.includes(inviteColleaguesTest) && (
        <InviteColleaguesModal
          open={isInviteColleaguesShow}
          onClose={() => setInviteColleaguesShow(false)}
          mode={inviteColleaguesTest}
        />
      )}
    </ThemeProvider>
  );
};

export default WeeklyDashboard;
